import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Phone, MapPin, Bird, Clock, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';
import { useLanguage } from '@/lib/i18n';

// Seuil desktop : ≥1024px (lg) pour éviter la compression sur tablette 768-1023px
const DESKTOP_BREAKPOINT = 1024;

/**
 * Sélecteur de langue — touch targets ≥ 44px (WCAG 2.5.5)
 */
function LangSwitch() {
  const { lang, setLang } = useLanguage();
  const langs = ['fr', 'en', 'es'];
  return (
    <div
      className="flex items-center rounded-full border border-garden-200 overflow-hidden text-xs font-medium"
      data-testid="lang-switch"
      role="group"
      aria-label="Sélection de la langue"
    >
      {langs.map(l => (
        <button
          key={l}
          onClick={() => setLang(l)}
          /* px-3 py-2.5 assure une zone de touche ≥ 44px sur mobile */
          className={`px-3 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-garden-800 ${
            lang === l ? 'bg-garden-800 text-white' : 'text-charcoal hover:bg-garden-100'
          }`}
          data-testid={`lang-${l}`}
          aria-label={`Langue : ${l.toUpperCase()}`}
          aria-pressed={lang === l}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const { t } = useLanguage();

  useEffect(() => {
    publicApi.getSettings().then(r => setSettings(r.data || {})).catch(() => {});
  }, []);

  /**
   * Fermeture automatique du menu mobile lors du passage en mode desktop.
   * Évite un état open=true fantôme si l'utilisateur redimensionne la fenêtre.
   */
  const handleResize = useCallback(() => {
    if (window.innerWidth >= DESKTOP_BREAKPOINT && open) {
      setOpen(false);
    }
  }, [open]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const ordersEnabled = settings.ordersModuleEnabled === true;
  const reservationsEnabled = settings.reservationsModuleEnabled === true;

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/carte', label: t('nav.menu') },
    ...(ordersEnabled ? [{ href: '/commander', label: t('nav.order') }] : []),
    { href: '/galerie', label: t('nav.gallery') },
    { href: '/avis', label: t('nav.reviews') },
    ...(reservationsEnabled ? [{ href: '/reservation', label: t('nav.reservation') }] : []),
  ];

  return (
    <nav
      role="navigation"
      aria-label="Menu principal"
      className="fixed top-4 left-1/2 -translate-x-1/2 w-[95%] max-w-5xl z-50 rounded-full border border-white/40 shadow-sm backdrop-blur-md bg-cream/80 px-6 py-3 flex items-center justify-between"
      data-testid="navbar"
    >
      <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
        {settings.restaurant_logo_url ? (
          <img
            src={settings.restaurant_logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.restaurant_logo_url}` : settings.restaurant_logo_url}
            alt={settings.name || 'Cote Terrasse'}
            className="h-8 w-auto object-contain"
            data-testid="navbar-logo-img"
          />
        ) : (
          <span className="font-accent text-2xl text-garden-800 hover:text-garden-600 transition-colors flex items-center gap-2">
            <Bird className="text-sunny fill-sunny/20" />
            {settings.name || 'Cote Terrasse'}
          </span>
        )}
      </Link>

      {/* ─── Desktop nav (≥1024px) ─────────────────────────────── */}
      <div className="hidden lg:flex items-center gap-1">
        {navLinks.map((link) => (
          <Link
            key={link.href}
            to={link.href}
            data-testid={`navbar-${link.href.replace('/', '') || 'home'}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-garden-800 ${
              location.pathname === link.href
                ? 'bg-garden-800 text-white'
                : 'text-charcoal hover:bg-garden-100 hover:text-garden-800'
            }`}
            aria-current={location.pathname === link.href ? 'page' : undefined}
          >
            {link.label}
          </Link>
        ))}
      </div>

      <div className="hidden lg:flex items-center gap-3">
        <LangSwitch />
        <a
          href="tel:0952675289"
          className="flex items-center gap-1 text-sm text-garden-800 hover:text-garden-600"
          data-testid="navbar-phone"
          aria-label="Appeler le restaurant au 09 52 67 52 89"
        >
          <Phone size={14} />
          <span>09 52 67 52 89</span>
        </a>
        {reservationsEnabled && (
          <Link to="/reservation">
            <Button
              data-testid="navbar-reserve-btn"
              className="bg-garden-800 hover:bg-garden-900 text-white rounded-full px-6 text-sm"
            >
              {t('nav.reserve')}
            </Button>
          </Link>
        )}
      </div>

      {/* ─── Mobile / Tablette nav (<1024px) ──────────────────── */}
      <div className="flex items-center gap-2 lg:hidden">
        <LangSwitch />
        <a
          href="tel:0952675289"
          className="p-2 bg-garden-800 text-white rounded-full shadow-lg"
          data-testid="navbar-mobile-phone"
          aria-label="Appeler le restaurant"
        >
          <Phone size={20} />
        </a>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="navbar-mobile-toggle"
              className="p-2 text-garden-800 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-garden-800 focus-visible:ring-offset-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={open ? 'Fermer le menu de navigation' : 'Ouvrir le menu de navigation'}
              aria-expanded={open}
              aria-controls="mobile-nav-menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </SheetTrigger>
          <SheetContent
            id="mobile-nav-menu"
            side="right"
            className="bg-cream w-[280px]"
            aria-label="Menu de navigation mobile"
          >
            {/* Titre accessible sr-only requis par Radix DialogContent pour les lecteurs d'écran */}
            <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
            <div className="flex flex-col gap-4 mt-8">
              {settings.restaurant_logo_url ? (
                <img
                  src={settings.restaurant_logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.restaurant_logo_url}` : settings.restaurant_logo_url}
                  alt={settings.name || 'Cote Terrasse'}
                  className="h-10 w-auto object-contain self-start mb-4"
                />
              ) : (
                <span className="font-accent text-2xl text-garden-800 mb-4">
                  {settings.name || 'Cote Terrasse'}
                </span>
              )}

              {/* Liens de navigation */}
              <nav aria-label="Navigation mobile">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setOpen(false)}
                    className={`flex items-center px-4 py-3 rounded-xl text-base font-medium transition-all mb-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-garden-800 ${
                      location.pathname === link.href
                        ? 'bg-garden-800 text-white'
                        : 'text-charcoal hover:bg-garden-100'
                    }`}
                    aria-current={location.pathname === link.href ? 'page' : undefined}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>

              {/* Infos pratiques */}
              <div className="mt-4 pt-4 border-t border-garden-200">
                <a
                  href="tel:0952675289"
                  className="flex items-center gap-2 text-garden-800 mb-3 min-h-[44px]"
                  aria-label="Appeler le restaurant au 09 52 67 52 89"
                >
                  <Phone size={16} /> 09 52 67 52 89
                </a>
                <div className="flex items-center gap-2 text-sunny font-semibold mb-3 min-h-[44px]">
                  <Clock size={16} /> Cuisine dès {settings.kitchen_opening_time || '11h30'}
                </div>
                <a
                  href="https://www.google.com/maps/place/198+Cours+de+l'Argonne,+33000+Bordeaux"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-garden-800 text-sm min-h-[44px]"
                  aria-label="Voir l'adresse sur Google Maps"
                >
                  <MapPin size={16} /> 198 Cours de l'Argonne
                </a>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}
